import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { BookOpen, Users, FileText, BookMarked, Search, X, Clock } from 'lucide-react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Input } from './ui/input';
import { Card } from './ui/card';
import LiveClock from './LiveClock';
import { useSEO } from '../context/SEOContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const Navigation = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { seoSettings } = useSEO();
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [surahs, setSurahs] = useState([]);
  const [meals, setMeals] = useState([]);
  const [reciters, setReciters] = useState([]);
  const [loading, setLoading] = useState(false);
  const [typedText, setTypedText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [textIndex, setTextIndex] = useState(0); // 0: domain, 1: slogan
  
  // Get dynamic texts from settings
  const headerLogoText = seoSettings?.headerLogoText || 'Kuran-ı Kerim';
  const headerLogoUrl = seoSettings?.headerLogoUrl || 'www.kuranikerim.se';
  const texts = [headerLogoUrl, 'Oku Anla Yaşa'];
  
  // Typewriter effect - alternating texts
  useEffect(() => {
    const typingSpeed = isDeleting ? 50 : 100;
    const currentText = texts[textIndex];
    
    const timer = setTimeout(() => {
      if (!isDeleting) {
        // Typing phase
        if (typedText.length < currentText.length) {
          setTypedText(currentText.substring(0, typedText.length + 1));
        } else {
          // Text complete, pause then start deleting
          setTimeout(() => setIsDeleting(true), 2000);
        }
      } else {
        // Deleting phase
        if (typedText.length > 0) {
          setTypedText(currentText.substring(0, typedText.length - 1));
        } else {
          // Deleted everything, move to next text
          setIsDeleting(false);
          setTextIndex((prev) => (prev + 1) % texts.length);
        }
      }
    }, typingSpeed);
    
    return () => clearTimeout(timer);
  }, [typedText, isDeleting, textIndex]);

  // Listen for search events from BottomNav
  useEffect(() => {
    const handleOpenSearch = () => setSearchOpen(true);
    window.addEventListener('openSearch', handleOpenSearch);
    return () => window.removeEventListener('openSearch', handleOpenSearch);
  }, []);

  // Load data when search opens
  useEffect(() => {
    if (searchOpen && searchQuery.length > 0) {
      loadSearchData();
    }
  }, [searchQuery, searchOpen]);

  const loadSearchData = async () => {
    setLoading(true);
    try {
      const [surahsRes, mealsRes, recitersRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/surahs`),
        fetch(`${BACKEND_URL}/api/meals`),
        fetch(`${BACKEND_URL}/api/reciters`)
      ]);

      if (surahsRes.ok) {
        const surahsData = await surahsRes.json();
        setSurahs(surahsData);
      }
      if (mealsRes.ok) {
        const mealsData = await mealsRes.json();
        setMeals(mealsData);
      }
      if (recitersRes.ok) {
        const recitersData = await recitersRes.json();
        setReciters(recitersData);
      }
    } catch (error) {
      console.error('Error loading search data:', error);
    } finally {
      setLoading(false);
    }
  };

  const navItems = [
    { name: 'Anasayfa', path: '/', icon: BookOpen },
    { name: 'Sureler', path: '/sureler', icon: BookMarked },
    { name: 'Hafizlar', path: '/hafizlar', icon: Users },
    { name: 'Meal', path: '/meal', icon: FileText },
    { name: 'Namaz Vakitleri', path: '/namaz-vakitleri', icon: Clock }
  ];

  // Search filtering
  const filteredSurahs = surahs.filter(surah =>
    surah.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (surah.nameArabic && surah.nameArabic.includes(searchQuery))
  ).slice(0, 5);

  const filteredMeals = meals.filter(meal =>
    meal.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    meal.author.toLowerCase().includes(searchQuery.toLowerCase())
  ).slice(0, 5);

  const filteredReciters = reciters.filter(reciter =>
    reciter.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (reciter.nameArabic && reciter.nameArabic.includes(searchQuery))
  ).slice(0, 5);

  const hasResults = filteredSurahs.length > 0 || filteredMeals.length > 0 || filteredReciters.length > 0;

  const handleResultClick = (type, id) => {
    setSearchOpen(false);
    setSearchQuery('');
    
    switch(type) {
      case 'surah':
        navigate(`/?surah=${id}`);
        break;
      case 'meal':
        navigate(`/meal/${id}`);
        break;
      case 'reciter':
        navigate(`/hafizlar/${id}`);
        break;
    }
  };

  return (
    <>
      <nav className="bg-[#F5EFE7] border-b-2 border-[#D5C4A1] sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-6">
          <div className="flex items-center justify-between h-14 sm:h-16">
            {/* Logo */}
            <div className="flex items-center flex-shrink-0">
              <Link to="/" className="flex items-center space-x-2">
                <BookOpen className="h-7 w-7 sm:h-8 sm:w-8 text-[#8B7355] flex-shrink-0" />
                <div className="flex flex-col min-w-[220px] sm:min-w-[280px]">
                  <span className="text-base sm:text-lg font-bold text-[#5C4033] leading-tight">
                    {headerLogoText}
                  </span>
                  <span className="text-[10px] sm:text-xs text-[#8B7355] font-mono leading-tight whitespace-nowrap">
                    {typedText}<span className="animate-pulse">|</span>
                  </span>
                </div>
              </Link>
            </div>

            {/* Desktop Navigation Items - Hidden on mobile */}
            <div className="hidden md:flex items-center space-x-1">
              {navItems.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path;
                return (
                  <Link key={item.path} to={item.path}>
                    <Button
                      variant="ghost"
                      size="sm"
                      className={`flex items-center space-x-2 ${
                        isActive 
                          ? 'bg-[#8B7355] text-white' 
                          : 'text-[#6B5744] hover:bg-[#E8DCC8]'
                      }`}
                    >
                      <Icon className="h-4 w-4" />
                      <span className="text-sm">{item.name}</span>
                    </Button>
                  </Link>
                );
              })}
            </div>

            {/* Right Side: Search (desktop only), Clock */}
            <div className="flex items-center space-x-2 sm:space-x-3">
              <Button 
                variant="ghost" 
                size="icon" 
                className="hidden md:flex text-[#6B5744] hover:bg-[#E8DCC8] h-9 w-9 sm:h-10 sm:w-10"
                onClick={() => setSearchOpen(true)}
              >
                <Search className="h-4 w-4 sm:h-5 sm:w-5" />
              </Button>
              <LiveClock />
            </div>
          </div>
        </div>
      </nav>

      {/* Search Modal */}
      <Dialog open={searchOpen} onOpenChange={setSearchOpen}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto bg-[#F9F6F1]">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-[#5C4033] mb-4">Arama</DialogTitle>
            <DialogDescription className="sr-only">
              Sure, meal veya hafiz arama penceresi
            </DialogDescription>
          </DialogHeader>

          {/* Search Input */}
          <div className="mb-6">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#8B7355] h-5 w-5" />
              <Input
                type="text"
                placeholder="Sure, meal veya hafiz ara..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 pr-10 bg-white border-[#D5C4A1] text-[#5C4033] placeholder:text-[#B8A894] h-12 text-base"
                autoFocus
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-[#8B7355] hover:text-[#5C4033]"
                >
                  <X className="h-5 w-5" />
                </button>
              )}
            </div>
          </div>

          {/* Search Results */}
          {searchQuery && (
            <div className="space-y-6">
              {/* Surahs */}
              {filteredSurahs.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold text-[#5C4033] mb-3">Sureler ({filteredSurahs.length})</h3>
                  <div className="space-y-2">
                    {filteredSurahs.slice(0, 5).map((surah, index) => (
                      <Link
                        key={surah.id}
                        to={`/?surah=${surah.slug || surah.id}`}
                        onClick={() => {
                          console.log('[Navigation] Surah click:', surah.name, '→', surah.slug || surah.id);
                          setSearchOpen(false);
                        }}
                      >
                        <Card className="bg-white border-[#E8DCC8] hover:border-[#D5C4A1] p-4 cursor-pointer">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-4">
                              <div className="w-10 h-10 bg-[#F5EFE7] border-2 border-[#D5C4A1] rounded-lg flex items-center justify-center">
                                <span className="text-sm font-bold text-[#5C4033]">{surah.order || index + 1}</span>
                              </div>
                              <div>
                                <p className="font-semibold text-[#5C4033]">{surah.name}</p>
                                <p className="text-sm text-[#8B7355]">{surah.verses} Ayet</p>
                              </div>
                            </div>
                            <p className="text-xl text-[#5C4033]" style={{ fontFamily: '"Traditional Arabic", serif' }}>
                              {surah.nameArabic}
                            </p>
                          </div>
                        </Card>
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              {/* Meals */}
              {filteredMeals.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold text-[#5C4033] mb-3">Mealler ({filteredMeals.length})</h3>
                  <div className="space-y-2">
                    {filteredMeals.slice(0, 5).map((meal) => (
                      <Link
                        key={meal.id}
                        to={`/meal/${meal.slug || meal.id}`}
                        onClick={() => setSearchOpen(false)}
                      >
                        <Card className="bg-white border-[#E8DCC8] hover:border-[#D5C4A1] p-4 cursor-pointer">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-semibold text-[#5C4033]">{meal.name}</p>
                              <p className="text-sm text-[#8B7355]">{meal.author}</p>
                            </div>
                            <p className="text-xl text-[#5C4033]" style={{ fontFamily: '"Traditional Arabic", serif' }}>
                              {meal.nameArabic}
                            </p>
                          </div>
                        </Card>
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              {/* Reciters */}
              {filteredReciters.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold text-[#5C4033] mb-3">Hafizlar ({filteredReciters.length})</h3>
                  <div className="space-y-2">
                    {filteredReciters.slice(0, 5).map((reciter) => (
                      <Link
                        key={reciter.id}
                        to={`/hafizlar/${reciter.slug || reciter.id}`}
                        onClick={() => setSearchOpen(false)}
                      >
                        <Card className="bg-white border-[#E8DCC8] hover:border-[#D5C4A1] p-4 cursor-pointer">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-semibold text-[#5C4033]">{reciter.name}</p>
                              <p className="text-sm text-[#8B7355]">{reciter.country}</p>
                            </div>
                            <p className="text-xl text-[#5C4033]" style={{ fontFamily: '"Traditional Arabic", serif' }}>
                              {reciter.nameArabic}
                            </p>
                          </div>
                        </Card>
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              {/* No Results */}
              {!hasResults && (
                <div className="text-center py-12">
                  <Search className="h-16 w-16 text-[#D5C4A1] mx-auto mb-4" />
                  <p className="text-[#8B7355]">Aradiginiz icin sonuc bulunamadi.</p>
                </div>
              )}
            </div>
          )}

          {/* Empty State */}
          {!searchQuery && (
            <div className="text-center py-12">
              <Search className="h-16 w-16 text-[#D5C4A1] mx-auto mb-4" />
              <p className="text-[#8B7355]">Sure, meal veya hafiz aramak icin yazmaya baslayin...</p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default Navigation;